import React, { createContext, useContext, useReducer, useEffect } from 'react';

const SettingsContext = createContext();

// Actions
const SET_SETTING = 'SET_SETTING';
const SET_MULTIPLE_SETTINGS = 'SET_MULTIPLE_SETTINGS';
const RESET_SETTINGS = 'RESET_SETTINGS';

// Initial state with default settings
const initialSettings = {
  // TravelIT branding (non-editable)
  appName: 'TravelIT',
  appVersion: '1.0.0',
  appCopyright: '© 2024 TravelIT',
  
  // Informations de l'agence (editable)
  agencyName: 'Agence de Voyage',
  agencyEmail: 'contact@example.com',
  agencyPhone: '+213 00 000 000',
  agencyAddress: 'Adresse de votre agence',
  agencyWebsite: 'www.votreagence.com',
  businessLicense: 'AGV-DZ-0000-000',
  
  // Paramètres système
  defaultCurrency: 'DZD',
  timeZone: 'Africa/Algiers',
  dateFormat: 'DD/MM/YYYY',
  language: 'fr',
  
  // Paramètres financiers
  taxRate: 19,
  commissionRate: 10,
  cancellationFee: 5000,
  
  // Taux de change (marché noir)
  exchangeRates: {
    EUR: {
      rate: 243,   // 1 EUR = 243 DZD
      lastUpdated: new Date().toISOString()
    },
    USD: {
      rate: 223,   // 1 USD = 223 DZD
      lastUpdated: new Date().toISOString()
    }
  },
  
  // Notifications
  emailNotifications: true,
  smsNotifications: false,
  pushNotifications: true,
  
  // Sécurité
  twoFactorAuth: false,
  sessionTimeout: 30,
  passwordPolicy: 'strict',
  
  // Paramètres d'affichage
  theme: 'light',
  sidebarCollapsed: false,
  
  // Paramètres métier
  defaultBookingStatus: 'confirmé',
  autoGenerateInvoices: true,
  sendConfirmationEmails: true,
  
  // Logo et branding
  agencyLogo: null
};

// Reducer
function settingsReducer(state, action) {
  switch (action.type) {
    case SET_SETTING:
      return {
        ...state,
        [action.field]: action.value
      };
    case SET_MULTIPLE_SETTINGS:
      return {
        ...state,
        ...action.settings
      };
    case RESET_SETTINGS:
      return initialSettings;
    default:
      return state;
  }
}

// Provider component
export const SettingsProvider = ({ children }) => {
  // Use lazy initialization to avoid unnecessary re-renders on first load
  const [settings, dispatch] = useReducer(settingsReducer, null, () => {
    // Try to load from localStorage first
    const savedSettings = localStorage.getItem('travelit-settings');
    if (savedSettings) {
      try {
        return { ...initialSettings, ...JSON.parse(savedSettings) };
      } catch (error) {
        console.error('Error loading settings from localStorage:', error);
      }
    }
    return initialSettings;
  });

  // Save to localStorage whenever settings change, but debounced
  useEffect(() => {
    const saveToStorage = () => {
      localStorage.setItem('travelit-settings', JSON.stringify(settings));
    };
    
    // Use debounce to avoid too many writes to localStorage
    const timeoutId = setTimeout(saveToStorage, 300);
    
    return () => clearTimeout(timeoutId);
  }, [settings]);

  const setSetting = (field, value) => {
    // Only dispatch if the value has actually changed
    if (settings[field] !== value) {
      dispatch({
        type: SET_SETTING,
        field,
        value
      });
    }
  };

  const setMultipleSettings = (newSettings) => {
    dispatch({
      type: SET_MULTIPLE_SETTINGS,
      settings: newSettings
    });
  };

  const resetSettings = () => {
    dispatch({ type: RESET_SETTINGS });
  };

  // Helper functions
  // Currency conversion functions - simplified to use a single exchange rate
  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    // If currencies are the same, no conversion needed
    if (fromCurrency === toCurrency) {
      return amount;
    }
    
    // Handle undefined, null or NaN values
    if (amount === undefined || amount === null || isNaN(amount) || amount === 0) {
      return 0;
    }
    
    // Both are foreign currencies, convert to DZD first then to target currency
    if (fromCurrency !== 'DZD' && toCurrency !== 'DZD') {
      const amountInDZD = convertToDZD(amount, fromCurrency);
      return convertFromDZD(amountInDZD, toCurrency);
    }
    
    // Convert from foreign currency to DZD
    if (fromCurrency !== 'DZD') {
      return convertToDZD(amount, fromCurrency);
    }
    
    // Convert from DZD to foreign currency
    return convertFromDZD(amount, toCurrency);
  };
  
  // Convert from a foreign currency to DZD
  const convertToDZD = (amount, fromCurrency) => {
    if (!settings.exchangeRates || !settings.exchangeRates[fromCurrency]) {
      console.error(`Exchange rate not found for ${fromCurrency}`);
      return amount; // Return original amount if rate not found
    }
    
    const rate = settings.exchangeRates[fromCurrency].rate;
    return amount * rate;
  };
  
  // Convert from DZD to a foreign currency
  const convertFromDZD = (amount, toCurrency) => {
    if (toCurrency === 'DZD') {
      return amount;
    }
    
    if (!settings.exchangeRates || !settings.exchangeRates[toCurrency]) {
      console.error(`Exchange rate not found for ${toCurrency}`);
      return amount; // Return original amount if rate not found
    }
    
    const rate = settings.exchangeRates[toCurrency].rate;
    return amount / rate;
  };
  
  // Get the current exchange rate between two currencies
  const getExchangeRate = (fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) {
      return 1;
    }
    
    if (!settings.exchangeRates) {
      return null;
    }
    
    if (fromCurrency === 'DZD' && settings.exchangeRates[toCurrency]) {
      return 1 / settings.exchangeRates[toCurrency].rate;
    }
    
    if (toCurrency === 'DZD' && settings.exchangeRates[fromCurrency]) {
      return settings.exchangeRates[fromCurrency].rate;
    }
    
    if (settings.exchangeRates[fromCurrency] && settings.exchangeRates[toCurrency]) {
      const toDZD = settings.exchangeRates[fromCurrency].rate;
      const fromDZD = 1 / settings.exchangeRates[toCurrency].rate;
      return toDZD * fromDZD;
    }
    
    return null;
  };
  
  // Format an amount in the specified currency
  const formatCurrency = (amount, currency = settings.defaultCurrency, showSymbol = true) => {
    // Handle undefined, null, boolean, or NaN values
    if (amount === undefined || amount === null || typeof amount === 'boolean' || isNaN(amount)) {
      return currency === 'DZD' ? '0 DZD' : currency === 'EUR' ? '0 €' : '0 $';
    }
    
    // Ensure amount is a number
    const numericAmount = Number(amount);
    
    // Format based on currency
    if (currency === 'DZD') {
      return `${numericAmount.toLocaleString()} ${showSymbol ? 'DZD' : ''}`;
    } else if (currency === 'EUR') {
      return showSymbol 
        ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(numericAmount)
        : new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(numericAmount);
    } else if (currency === 'USD') {
      return showSymbol
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(numericAmount)
        : new Intl.NumberFormat('en-US', { minimumFractionDigits: 0 }).format(numericAmount);
    }
    
    try {
      // Default formatter
      const formatter = new Intl.NumberFormat('fr-DZ', {
        style: showSymbol ? 'currency' : 'decimal',
        currency: typeof currency === 'string' ? currency : 'DZD',
        minimumFractionDigits: 0
      });
      
      return formatter.format(numericAmount);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return `${numericAmount.toLocaleString()} ${showSymbol ? (typeof currency === 'string' ? currency : 'DZD') : ''}`;
    }
  };

  const formatDate = (date) => {
    const dateObj = new Date(date);
    const format = settings.dateFormat;
    
    switch (format) {
      case 'DD/MM/YYYY':
        return dateObj.toLocaleDateString('fr-FR');
      case 'MM/DD/YYYY':
        return dateObj.toLocaleDateString('en-US');
      case 'YYYY-MM-DD':
        return dateObj.toISOString().split('T')[0];
      default:
        return dateObj.toLocaleDateString('fr-FR');
    }
  };

  const getLanguageText = (translations) => {
    return translations[settings.language] || translations.fr || translations.en;
  };

  const calculateTotalWithTax = (amount) => {
    return amount * (1 + settings.taxRate / 100);
  };

  const calculateCommission = (amount) => {
    return amount * (settings.commissionRate / 100);
  };

  const uploadLogo = (file) => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('Aucun fichier sélectionné'));
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        reject(new Error('Format de fichier non supporté. Utilisez JPG, PNG, GIF ou WebP.'));
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        reject(new Error('Le fichier est trop volumineux. Taille maximale: 5MB.'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const logoDataUrl = e.target.result;
        setSetting('agencyLogo', logoDataUrl);
        resolve(logoDataUrl);
      };
      reader.onerror = () => {
        reject(new Error('Erreur lors de la lecture du fichier'));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeLogo = () => {
    setSetting('agencyLogo', null);
  };

  const value = {
    settings,
    setSetting,
    setMultipleSettings,
    resetSettings,
    formatCurrency,
    formatDate,
    getLanguageText,
    calculateTotalWithTax,
    calculateCommission,
    uploadLogo,
    removeLogo,
    // New currency functions
    convertCurrency,
    convertToDZD,
    convertFromDZD,
    getExchangeRate
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// Hook to use settings
export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Export SettingsContext as both named and default export
export { SettingsContext };
export default SettingsContext; 